import { useEffect, useMemo, useRef, useState } from 'react';
import Datatable from '@/components/common/Datatable';
import { Box, Divider, HStack, Icon, Text, useDisclosure } from '@chakra-ui/react';
import { Column } from 'react-table';
import Button from '@/components/common/Button';
import FormModal from '@/components/common/FormModal';
import useCustomToast from '@/components/common/Toast';
import useErrorHandler from '@/utils/errorHandler';
import { AxiosError } from 'axios';
import deleteIcon from '../../assets/svg/delete-bg.svg?react';
import editIcon from '../../assets/svg/editIcon.svg?react';

import withAuth from '@/HOCs/withAuth';
import {
  useCreateUser,
  useDeleteUser,
  useGetUserList,
  useGetUserRoles,
  useGetUserRoleTitles,
  useUpdateUser,
} from '@/services/userManagement';
import { User } from '@/services/userManagement/types';
import StatusBadge from '@/components/common/StatusBadge';
import AddUserForm, { IAddUserData } from './AddUserForm';
import CustomConfirmationModal from '@/components/common/ConfirmationModal';
import ConfirmDeleteIcon from '@/assets/svg/delete-bg.svg?react';
import { formatApiDateToDisplay } from '@/utils/formatDateString';

const UserManagement = () => {
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<{
    start_date?: string;
    end_date?: string;
    category?: string;
    subcategory?: string;
    assign_title?: string;
    user_status?: boolean | null;
  }>({
    start_date: '',
    end_date: '',
    assign_title: '',
    user_status: null,
  });
  const { showToast } = useCustomToast();
  const { handleError } = useErrorHandler();
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [editUserData, setEditUserData] = useState<User | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { isOpen: isNewUserOpen, onOpen: onNewUserOpen, onClose: onNewUserClose } = useDisclosure();

  const {
    data: users,
    refetch,
    isLoading,
    isError,
  } = useGetUserList({
    page: pagination.page,
    query,
    ...filters,
  });

  const { data: roles } = useGetUserRoles();
  const { data: roleTitles } = useGetUserRoleTitles();

  const { mutateAsync: createUser, isLoading: isCreatingUser, isSuccess } = useCreateUser();

  const { mutateAsync: updateUser, isLoading: isUpdatingUser } = useUpdateUser();

  const { mutateAsync: deleteUser, isLoading: isDeletingUser } = useDeleteUser();

  useEffect(() => {
    if (users) {
      setPagination((prev) => ({
        ...prev,
        pageSize: 10,
        totalCount: users.count,
        totalPages: users.page_count,
      }));
    }
  }, [users, refetch, users]);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleSearch = (searchTerm: string) => {
    setQuery(searchTerm);
    setPagination((prev) => ({ ...prev, page: 1 }));
    refetch();
  };

  const applyFilters = () => {
    refetch();
  };

  const resetFilters = () => {
    setFilters({
      assign_title: '',
      user_status: null,
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  useEffect(() => {
    if (filters.assign_title === '' && filters.user_status === null) {
      refetch();
    }
  }, [filters, pagination.page]);

  const submitRef = useRef<() => void>(null);

  const userSubmit = async (data: IAddUserData) => {
    const uniqueRoles = Array.from(new Map(data.role.map((r) => [r.role_id, r])).values());

    const sanitizedData = { ...data, role: uniqueRoles };

    if (editUserData) {
      const editPayload = { ...sanitizedData, id: editUserData.id };
      try {
        await updateUser(editPayload);
        onNewUserClose();
        showToast('success', 'User Updated Successfully.');
      } catch (err: unknown) {
        if (err instanceof AxiosError) {
          handleError(err);
        }
      }
    } else {
      try {
        await createUser(sanitizedData);
        onNewUserClose();
        showToast('success', 'User Created Successfully.');
      } catch (err: unknown) {
        if (err instanceof AxiosError) {
          handleError(err);
        }
      }
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedUserId !== null) {
      try {
        deleteUser(selectedUserId).then(() => {
          setSelectedUserId(null);
          onClose();
          showToast('success', 'User Deleted Successfully');
        });
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          handleError(error);
        }
      }
    } else return;
  };

  const columns: Column<User>[] = useMemo(
    () => [
      {
        Header: 'User Name',
        Cell: ({ row }: { row: { original: User } }) => {
          const { first_name, last_name } = row.original;
          const full_name = `${first_name} ${last_name}`;
          return full_name ? (
            <Text minWidth={'150px'}>{full_name}</Text>
          ) : (
            <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
          );
        },
      },
      {
        Header: 'Role',
        Cell: ({ row }: { row: { original: User } }) => {
          const role = row.original.assigned_title;
          return role ? (
            <Text minWidth={'50px'}>{role}</Text>
          ) : (
            <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
          );
        },
      },
      {
        Header: 'Email Address',
        Cell: ({ row }: { row: { original: User } }) => {
          const email = row.original.email;
          return email ? (
            <Text minWidth={'100px'}>{email}</Text>
          ) : (
            <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
          );
        },
      },
      {
        Header: 'Status',
        Cell: ({ row }: { row: { original: User } }) => {
          const status = row.original.is_active ? 'Active' : 'Inactive';
          return status ? (
            <StatusBadge status={status} width="min-content" />
          ) : (
            <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
          );
        },
      },
      {
        Header: 'Access To',
        Cell: ({ row }: { row: { original: User } }) => {
          const permissions = row.original.roles;
          const permissionNames = permissions.map((role) => role.name).join(', ');
          return permissionNames ? (
            <Text minWidth={'200px'}>{permissionNames}</Text>
          ) : (
            <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
          );
        },
      },
      {
        Header: 'Last Login',
        Cell: ({ row }: { row: { original: User } }) => {
          const lastLogin = row.original.last_login;
          return lastLogin ? (
            <Text minWidth={'200px'}>{formatApiDateToDisplay(lastLogin)}</Text>
          ) : (
            <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
          );
        },
      },
      {
        Header: 'Actions',
        Cell: ({ row }: { row: { original: User } }) => {
          return (
            <HStack spacing={4}>
              <Icon
                as={editIcon}
                boxSize={5}
                color={'text.primary'}
                cursor={'pointer'}
                onClick={() => {
                  setEditUserData(row.original);
                  onNewUserOpen();
                }}
              />
              <Icon
                as={deleteIcon}
                boxSize={5}
                color={'status.error'}
                cursor={'pointer'}
                onClick={() => {
                  setSelectedUserId(row.original.id);
                  onOpen();
                }}
              />
            </HStack>
          );
        },
      },
    ],
    [users],
  );
  return (
    <Box p={6} fontFamily={'primary'}>
      <Datatable
        data={users?.results ?? []}
        isLoading={isLoading}
        isError={isError}
        columns={columns}
        headerButton={<Button onClick={onNewUserOpen}>+ New User</Button>}
        pagination={{
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalCount: pagination.totalCount,
          totalPages: pagination.totalPages,
        }}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        filters={filters}
        setFilters={setFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
        hideCategoryFilter={true}
        sticky={true}
        rolesFilter={roleTitles}
      />
      <FormModal
        title={editUserData ? 'Edit User' : 'Add User'}
        isOpen={isNewUserOpen}
        size="2xl"
        onConfirm={() => submitRef.current && submitRef.current()}
        isLoading={isCreatingUser || isUpdatingUser}
        children={
          <>
            <AddUserForm
              ref={submitRef}
              onSubmit={userSubmit}
              roles={roles ?? []}
              isSuccess={isSuccess}
              editUserData={editUserData}
            />
          </>
        }
        onClose={() => {
          setEditUserData(null);
          onNewUserClose();
        }}
      />
      <CustomConfirmationModal
        isLoading={isDeletingUser}
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleDeleteConfirm}
        icon={ConfirmDeleteIcon}
        title="Delete User?"
        subtitle="Are you sure you want to delete this user?"
        description="This action cannot be undone, and you won't be able to track this user anywhere. This will remove the user and related permissions permanently."
        buttonText="Confirm"
      />
    </Box>
  );
};

export default withAuth(UserManagement);
